/**
 * Shared utilities for Project Detail Page components
 *
 * Contains:
 * - Helper components (InfoPill, StatusPill, StageIndicator, NoteCard)
 * - Helper functions (formatDate, formatTimeAgo, etc.)
 * - Constants (WORKFLOW_STAGES)
 */

// SKU workflow stages for the progress matrix
export const WORKFLOW_STAGES = [
  { id: 'art', label: 'Art', shortLabel: 'ART' },
  { id: 'proof', label: 'Proofing', shortLabel: 'PRF' },
  { id: 'approval', label: 'Approval', shortLabel: 'APR' },
  { id: 'print', label: 'Printing', shortLabel: 'PRT' },
  { id: 'assembly', label: 'Assembly', shortLabel: 'ASM' },
  { id: 'ship', label: 'Shipping', shortLabel: 'SHP' },
];

/* ============ Helper Components ============ */

export function InfoPill({ label, value, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-md ${className}`}>
      <span className="text-gray-500">{label}:</span>
      <span className="font-medium text-gray-700">{value}</span>
    </span>
  );
}

export function StatusPill({ status }) {
  const statusConfig = {
    ready: { label: 'Ready', bg: 'bg-blue-100', text: 'text-blue-700' },
    in_progress: { label: 'In Progress', bg: 'bg-yellow-100', text: 'text-yellow-700' },
    awaiting_approval: { label: 'Awaiting Approval', bg: 'bg-purple-100', text: 'text-purple-700' },
    approved: { label: 'Approved', bg: 'bg-green-100', text: 'text-green-700' },
    printing: { label: 'Printing', bg: 'bg-cyan-100', text: 'text-cyan-700' },
    complete: { label: 'Complete', bg: 'bg-slate-100', text: 'text-gray-700' },
  };

  const config = statusConfig[status] || { label: status || '—', bg: 'bg-slate-100', text: 'text-gray-600' };

  return (
    <span className={`px-2 py-1 rounded-md text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

export function StageIndicator({ stage, skuStage }) {
  // Determine status based on current SKU stage
  const stageOrder = ['art', 'proof', 'approval', 'print', 'assembly', 'ship'];
  const currentIndex = stageOrder.indexOf(skuStage);
  const thisIndex = stageOrder.indexOf(stage);

  let status = 'pending';
  if (thisIndex < currentIndex) status = 'complete';
  else if (thisIndex === currentIndex) status = 'active';

  const styles = {
    complete: 'bg-green-500',
    active: 'bg-blue-500 animate-pulse',
    pending: 'border-2 border-gray-300 bg-white',
  };

  return (
    <div className={`w-3 h-3 rounded-full ${styles[status]} mx-auto`} />
  );
}

export function NoteCard({ note }) {
  const isQuestion = note.note_type === 'question';
  const bgColor = isQuestion
    ? 'bg-purple-50 border-purple-200'
    : 'bg-slate-200 border-gray-200';

  return (
    <div className={`p-3 rounded-lg border ${bgColor}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-700">
            {note.author_name || 'Unknown'}
          </span>
          {isQuestion && (
            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-medium">
              Question
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500">
          {formatTimeAgo(note.created_at)}
        </span>
      </div>
      <p className="text-sm text-gray-700">{note.content}</p>
    </div>
  );
}

/* ============ Helper Functions ============ */

export function formatDate(dateStr) {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function formatDateShort(dateStr) {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function isOverdue(dateStr) {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

export function formatStatus(status) {
  const statusMap = {
    ready: 'Ready to Work',
    in_progress: 'In Progress',
    awaiting_approval: 'Awaiting Approval',
    approved: 'Approved',
    printing: 'Printing',
    complete: 'Complete',
  };
  return statusMap[status] || status || '—';
}

export function formatTimeAgo(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function getSkuStage(sku, wo) {
  // Determine SKU stage based on work order status
  // This is a simplified version - in production, each SKU would have its own stage
  const status = wo.pa_status || wo.paStatus;
  const statusToStage = {
    ready: 'art',
    in_progress: 'proof',
    awaiting_approval: 'approval',
    approved: 'print',
    printing: 'print',
    complete: 'assembly',
  };
  return statusToStage[status] || 'art';
}

/**
 * Get file type from URL
 * @param {string} url - SharePoint or file URL
 * @returns {'word' | 'excel' | 'pdf' | 'unknown'}
 */
export function getFileType(url) {
  if (!url) return 'unknown';
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes('/:w:/') || lowerUrl.includes('.docx') || lowerUrl.includes('.doc')) return 'word';
  if (lowerUrl.includes('/:x:/') || lowerUrl.includes('.xlsx') || lowerUrl.includes('.xls')) return 'excel';
  if (lowerUrl.includes('.pdf')) return 'pdf';
  return 'unknown';
}

/**
 * Convert SharePoint URL to Office Online embed URL
 * Supports Word (.docx, .doc) and Excel (.xlsx, .xls) documents
 *
 * @param {string} sharePointUrl - Original SharePoint URL
 * @returns {string|null} Embed URL or null if not embeddable
 */
export function getOfficeOnlineUrl(sharePointUrl) {
  if (!sharePointUrl) return null;

  try {
    // Strip existing query params consistently
    const cleanUrl = sharePointUrl.replace(/\?.*$/, '');

    // Word documents (/:w:/ pattern or .docx/.doc extension)
    if (sharePointUrl.includes('/:w:/') || cleanUrl.endsWith('.docx') || cleanUrl.endsWith('.doc')) {
      return cleanUrl + '?action=embedview';
    }

    // Excel documents (/:x:/ pattern or .xlsx/.xls extension)
    if (sharePointUrl.includes('/:x:/') || cleanUrl.endsWith('.xlsx') || cleanUrl.endsWith('.xls')) {
      return cleanUrl + '?action=embedview';
    }

    // Unknown type - return null (will show fallback)
    return null;
  } catch (err) {
    console.error('Error converting SharePoint URL:', err);
    return null;
  }
}
